import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Float } from "@react-three/drei";
import { ArrowUpRight } from "lucide-react";
import { CanvasInView, Reveal, SectionTag, scrollToId } from "./Reveal";

const METAL = { metalness: 0.9, roughness: 0.22 };

function Spin({ children, speed = 0.45 }) {
  const r = useRef();
  useFrame((_, d) => {
    if (r.current) r.current.rotation.y += d * speed;
  });
  return <group ref={r}>{children}</group>;
}

function AviationObj() {
  return (
    <mesh>
      <torusKnotGeometry args={[0.8, 0.24, 180, 24]} />
      <meshStandardMaterial color="#c8d4e2" {...METAL} />
    </mesh>
  );
}

function IndustrialObj() {
  return (
    <group>
      <mesh position={[-0.7, -0.25, 0]}>
        <cylinderGeometry args={[0.55, 0.55, 1.5, 32]} />
        <meshStandardMaterial color="#b6c2d2" {...METAL} />
      </mesh>
      <mesh position={[-0.7, 0.28, 0]}>
        <cylinderGeometry args={[0.57, 0.57, 0.06, 32]} />
        <meshStandardMaterial color="#D9A441" emissive="#D9A441" emissiveIntensity={0.9} />
      </mesh>
      <mesh position={[0.6, -0.55, 0.25]}>
        <cylinderGeometry args={[0.38, 0.38, 1.0, 32]} />
        <meshStandardMaterial color="#93a3b6" {...METAL} />
      </mesh>
      <mesh position={[0.6, -0.02, 0.25]}>
        <cylinderGeometry args={[0.4, 0.4, 0.05, 32]} />
        <meshStandardMaterial color="#00E5FF" emissive="#00E5FF" emissiveIntensity={0.8} />
      </mesh>
    </group>
  );
}

function MarineObj() {
  return (
    <group rotation={[0, 0.4, 0]}>
      <mesh position={[0, -0.35, 0]}>
        <boxGeometry args={[2.5, 0.5, 0.8]} />
        <meshStandardMaterial color="#12263f" metalness={0.7} roughness={0.35} />
      </mesh>
      <mesh position={[0, -0.08, 0]}>
        <boxGeometry args={[2.2, 0.06, 0.7]} />
        <meshStandardMaterial color="#D9A441" emissive="#D9A441" emissiveIntensity={0.7} />
      </mesh>
      <mesh position={[-0.75, 0.14, 0]}>
        <boxGeometry args={[0.5, 0.5, 0.55]} />
        <meshStandardMaterial color="#c8d4e2" {...METAL} />
      </mesh>
      <mesh position={[-0.95, 0.52, 0]}>
        <cylinderGeometry args={[0.08, 0.1, 0.35, 16]} />
        <meshStandardMaterial color="#8d9cae" {...METAL} />
      </mesh>
    </group>
  );
}

function NetworkObj() {
  const orbit = useRef();
  useFrame((_, d) => {
    if (orbit.current) orbit.current.rotation.y += d * 0.8;
  });
  const nodes = [
    [1.4, 0, 0],
    [-1.4, 0, 0],
    [0, 0, 1.4],
    [0, 0, -1.4],
  ];
  return (
    <group>
      <mesh>
        <icosahedronGeometry args={[0.75, 1]} />
        <meshStandardMaterial color="#0d1b2e" wireframe emissive="#00E5FF" emissiveIntensity={0.4} />
      </mesh>
      <mesh>
        <sphereGeometry args={[0.16, 16, 16]} />
        <meshBasicMaterial color="#F0C05A" />
      </mesh>
      <group ref={orbit}>
        {nodes.map((p, i) => (
          <mesh key={i} position={p}>
            <sphereGeometry args={[0.09, 12, 12]} />
            <meshBasicMaterial color="#00E5FF" />
          </mesh>
        ))}
      </group>
    </group>
  );
}

function MiniScene({ type }) {
  return (
    <CanvasInView className="h-44 md:h-52 w-full" camera={{ position: [0, 0.7, 4.4], fov: 38 }} dpr={[1, 1.5]}>
      <ambientLight intensity={0.3} />
      <pointLight position={[4, 4, 4]} intensity={22} color="#F0C05A" />
      <pointLight position={[-4, -2, 3]} intensity={12} color="#00E5FF" />
      <Float speed={1.6} rotationIntensity={0.5} floatIntensity={1.1}>
        <Spin speed={type === "network" ? 0.25 : 0.45}>
          {type === "aviation" && <AviationObj />}
          {type === "industrial" && <IndustrialObj />}
          {type === "marine" && <MarineObj />}
          {type === "network" && <NetworkObj />}
        </Spin>
      </Float>
    </CanvasInView>
  );
}

const CARDS = [
  {
    key: "aviation",
    title: "Aviation Fuel",
    text: "Jet A-1 and aviation-related petroleum products for airlines and aviation operators.",
    img: "https://images.pexels.com/photos/9861441/pexels-photo-9861441.jpeg?auto=compress&cs=tinysrgb&w=1260",
    span: "md:col-span-7",
  },
  {
    key: "industrial",
    title: "Industrial & Fleet Energy",
    text: "Diesel and petroleum products for businesses, fleets and industrial operations.",
    img: "https://images.pexels.com/photos/3105242/pexels-photo-3105242.jpeg?auto=compress&cs=tinysrgb&w=1260",
    span: "md:col-span-5",
  },
  {
    key: "marine",
    title: "Marine Fuel",
    text: "Marine fuel and procurement solutions for vessels and offshore operations.",
    img: "https://images.pexels.com/photos/12810602/pexels-photo-12810602.jpeg?auto=compress&cs=tinysrgb&w=1260",
    span: "md:col-span-5",
  },
  {
    key: "network",
    title: "Custom Procurement",
    text: "Need a petroleum or energy product that isn\u2019t listed? Tell us what you need — our team will help identify suitable supply options.",
    img: "https://images.pexels.com/photos/30820142/pexels-photo-30820142.jpeg?auto=compress&cs=tinysrgb&w=1260",
    span: "md:col-span-7",
  },
];

export default function SolutionsBento() {
  return (
    <section id="solutions" data-testid="solutions-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionTag number="03" label="Solutions" />
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-outfit text-3xl md:text-5xl font-extralight tracking-tight text-white max-w-xl">
              Your energy <span className="text-gold">procurement partner.</span>
            </h2>
            <p className="text-white/55 max-w-md text-base leading-relaxed">
              Ajemi helps you source and procure petroleum products — without searching
              through multiple suppliers yourself.
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {CARDS.map((card, i) => (
            <Reveal key={card.key} delay={0.08 * i} className={card.span}>
              <div
                data-testid={`solution-card-${card.key}`}
                className="group relative h-full glass-panel rounded-2xl overflow-hidden hover:border-gold/30 transition-all duration-500 hover:-translate-y-1"
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                  <img src={card.img} alt="" loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-ink/40" />
                </div>
                <div className="relative p-8 md:p-10 flex flex-col h-full">
                  <div className="relative">
                    <MiniScene type={card.key} />
                  </div>
                  <div className="mt-auto pt-6">
                    <div className="flex items-center justify-between">
                      <h3 className="font-outfit text-2xl md:text-3xl font-light text-white">{card.title}</h3>
                      <button
                        data-testid={`solution-card-${card.key}-cta`}
                        onClick={() => scrollToId("quote")}
                        className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/50 group-hover:bg-gold group-hover:text-ink group-hover:border-gold transition-all duration-500"
                        aria-label={`Request ${card.title} procurement`}
                      >
                        <ArrowUpRight className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-white/55 text-sm md:text-base mt-3 max-w-md leading-relaxed">{card.text}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm" data-testid="solutions-service-links">
            <button onClick={() => scrollToId("managed-supply")} className="text-gold/80 hover:text-gold transition-colors font-outfit tracking-wide" data-testid="link-managed-recurring">
              Managed Recurring Supply →
            </button>
            <button onClick={() => scrollToId("managed-supply")} className="text-gold/80 hover:text-gold transition-colors font-outfit tracking-wide" data-testid="link-multi-location">
              Multi-Location Procurement →
            </button>
            <button onClick={() => scrollToId("international")} className="text-gold/80 hover:text-gold transition-colors font-outfit tracking-wide" data-testid="link-international-procurement">
              International Procurement →
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
